import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { FileDownload, Print } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DynamicTable from "../lab-dynamic-table";
import DateRangeSelector from "../DateRangeSelector";
import { formatCurrency } from "../../utils/numberUtils";

interface DoctorRevenueData {
  _id: string;
  doctorName: string;
  grandTotal: number;
  paidAmount: number;
  allDrShares: number;
  hospitalShare: number;
  totalCount: number;
}

// Doctor interface
interface Doctor {
  _id: string;
  fullName: string;
}

export const DoctorRevenueSummaryTab: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];

  const [summaryData, setSummaryData] = useState<DoctorRevenueData[]>([]);
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<string>("");

  // State for date filtering
  const [dateRange, setDateRange] = useState("Today");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // State for pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
    setPage(0);
  };

  const fetchDoctors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/users/getAllSharerDoctors`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const fetchedDoc = response.data?.data || [];
      const uniqueDoc = Array.from(
        new Map(fetchedDoc.map((item: any) => [item.fullName, item])).values()
      );
      setDoctors(uniqueDoc as Doctor[]);
    } catch (err) {
      console.error("Error fetching doctors:", err);
    }
  };

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const params: any = {
        fromDate,
        toDate,
        pageNo: page + 1,
        count: rowsPerPage,
      };
      if (selectedDoctor) params.doctorId = selectedDoctor;

      const response = await axios.get(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/getDoctorSummary`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setSummaryData(response.data.data || []);
        setTotalCount(response.data.totalCount || 0);
      } else {
        toast.error(response.data.message || "Failed to fetch doctor revenue summary");
      }
    } catch (err: any) {
      toast.error(err.message || "Error fetching doctor revenue summary");
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  useEffect(() => {
    fetchSummaryData();
  }, [fromDate, toDate, page, rowsPerPage, selectedDoctor]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExcelExport = () => {
    if (!summaryData || summaryData?.length === 0) {
      toast.warn("No data to export.");
      return;
    }

    const dataWithTotals = [
      ...summaryData.map((item) => ({
        "Doctor Name": item.doctorName,
        "Grand Total": item.grandTotal,
        "Paid Amount": item.paidAmount,
        "Doctor Share": item.allDrShares,
        "Hospital Share": item.hospitalShare,
      })),
      {
        "Doctor Name": "TOTALS",
        "Grand Total": totalGrand,
        "Paid Amount": totalPaid,
        "Doctor Share": totalDoctorShare,
        "Hospital Share": totalHospitalShare,
      }
    ];

    const worksheet = xlsx.utils.json_to_sheet(dataWithTotals);
    const workbook = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(workbook, worksheet, "Doctor Revenue Summary");
    xlsx.writeFile(workbook, `Doctor_Revenue_Summary_${fromDate}_to_${toDate}.xlsx`);
  };

  const handlePrint = () => {
    if (!summaryData || summaryData?.length === 0) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 8;
    let yPosition = margin;

    // Professional header with minimal, clean design
    doc
      .setFontSize(10)
      .setFont("helvetica", "bold")
      .setTextColor(25, 55, 85)
      .text("DOCTOR REVENUE SUMMARY", pageWidth / 2, yPosition, {
        align: "center",
      });

    yPosition += 6;
    doc
      .setDrawColor(25, 55, 85)
      .setLineWidth(0.5)
      .line(margin, yPosition, pageWidth - margin, yPosition);

    // Subtle metadata section with smaller font
    yPosition += 3;
    doc
      .setFontSize(6.5)
      .setFont("helvetica", "normal")
      .setTextColor(80, 80, 80)
      .text(
        `Report Period: ${fromDate} to ${toDate} | Generated: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
        pageWidth / 2,
        yPosition,
        { align: "center" }
      );

    yPosition += 6;

    const head = [
      [
        "DOCTOR NAME",
        "GRAND TOTAL",
        "PAID AMOUNT",
        "DOCTOR SHARE",
        "HOSPITAL SHARE",
        
      ],
    ];

    const body = summaryData?.map(row => [
      row.doctorName,
      formatCurrency(row.grandTotal || 0),
      formatCurrency(row.paidAmount || 0),
      formatCurrency(row.allDrShares || 0),
      formatCurrency(row.hospitalShare || 0),
      formatCurrency(row.totalCount || 0),
    ]);

    // Add totals row
    body.push([
      "TOTALS",
      formatCurrency(totalGrand),
      formatCurrency(totalPaid),
      formatCurrency(totalDoctorShare),
      formatCurrency(totalHospitalShare),
      // formatCurrency(docCount),
    ]);

    // Clean, professional table styling
    autoTable(doc, {
      startY: yPosition,
      head,
      body,
      headStyles: {
        fillColor: [25, 55, 85],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 6,
        halign: 'left',
        cellPadding: 2
      },
      bodyStyles: {
        fontSize: 5.5,
        textColor: [40, 40, 40],
        cellPadding: 1.8
      },
      columnStyles: {
        0: { halign: "left", cellWidth: 50 }, // Doctor Name
        1: { halign: "left", cellWidth: 35 }, // Grand Total
        2: { halign: "left", cellWidth: 35 }, // Paid Amount
        3: { halign: "left", cellWidth: 35 }, // Doctor Share
        4: { halign: "left", cellWidth: 35 }, // Hospital Share
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      margin: { top: margin, right: margin, bottom: margin, left: margin },
      didParseCell: function (data) {
        // Professional total row with premium styling
        if (data.row.index === body.length - 1) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [25, 55, 85];
        }
      },
      didDrawPage: function (data) {
        // Add subtle footer with page number
        const pageSize = doc.internal.pageSize;
        const pageCount = doc.getNumberOfPages();
        const pageNum = data.pageNumber;

        doc.setFontSize(5);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${pageNum} of ${pageCount}`,
          pageSize.getWidth() / 2,
          pageSize.getHeight() - 4,
          { align: "center" }
        );
      }
    });

    doc.autoPrint();
    const blob = doc.output("blob");
    const blobUrl = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);
    
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(blobUrl);
      }
      window.removeEventListener("focus", cleanup);
    };
    
    window.addEventListener("focus", cleanup);
    iframe.src = blobUrl;
    
    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (win) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Error triggering print dialog:", e);
        cleanup();
      }
    };
    
    // Fallback print trigger
    setTimeout(() => {
      try {
        const win = iframe.contentWindow;
        if (win && document.body.contains(iframe)) {
          win.focus();
          win.print();
        }
      } catch (e) {
        console.error("Fallback print attempt failed:", e);
        cleanup();
      }
    }, 1000);
  };

  const tableData = summaryData?.map(item => [
    item?.doctorName || "N/A",
    formatCurrency(item?.grandTotal),
    formatCurrency(item?.paidAmount),
    formatCurrency(item?.allDrShares),
    formatCurrency(item?.hospitalShare),
    formatCurrency(item?.totalCount),
  ]);

  // Calculate totals
  const totalGrand = summaryData.reduce(
    (sum, item) => sum + (item?.grandTotal || 0),
    0
  );
  const totalPaid = summaryData.reduce(
    (sum, item) => sum + (item?.paidAmount || 0),
    0
  );
  const totalDoctorShare = summaryData.reduce(
    (sum, item) => sum + (item?.allDrShares || 0),
    0
  );
  const totalHospitalShare = summaryData.reduce(
    (sum, item) => sum + (item?.hospitalShare || 0),
    0
  );

  const docCount = summaryData.reduce(
    (sum, item) => sum + (item?.totalCount || 0),
    0
  );

  const tableDataWithTotals = [
    ...tableData,
    [
      <strong>Totals</strong>,
      <strong>{formatCurrency(totalGrand)}</strong>,
      <strong>{formatCurrency(totalPaid)}</strong>,
      <strong>{formatCurrency(totalDoctorShare)}</strong>,
      <strong>{formatCurrency(totalHospitalShare)}</strong>,
      <strong>{formatCurrency(docCount)}</strong>,
    ],
  ];

  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  return (
    <Box sx={{ background: "#fff", borderRadius: "12px" }}>
      <Box
        sx={{
          p: 1,
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Export data to Excel file" arrow placement="top">
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={handleExcelExport}
              sx={gradientButtonStyle}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip title="Print doctor revenue report" arrow placement="top">
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={gradientButtonStyle}
            >
              Print
            </Button>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography sx={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#000",
              mb: 0.5
            }}>
              Filter by Doctor
            </Typography>
            <Autocomplete
              size="small"
              className="custom-input"
              fullWidth
              options={doctors || []}
              value={doctors.find((doc) => doc._id === selectedDoctor) || null}
              onChange={(event, newValue) => {
                setSelectedDoctor(newValue?._id || "");
                setPage(0);
              }}
              getOptionLabel={(option) => option.fullName || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="All Doctors"
                  className="custom-input"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: 1,
          maxHeight: "60vh",
          overflowY: "hidden",
        }}
      >
        <DynamicTable
          headers={[
            "Doctor Name",
            "Grand Total",
            "Paid Amount",
            "Doctor Share",
            "Hospital Share",
            "Total Count",
          ]}
          data={tableDataWithTotals}
          loading={loading}
          height={"60vh"}
        //   page={page}
        //   rowsPerPage={rowsPerPage}
        //   totalRows={totalCount}
        //   handleChangePage={handleChangePage}
        //   handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};